import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Typography, Card } from "@material-tailwind/react";

import { getMoneyFormat, getTextFormat } from "../../../../utils/formatViews";
import Loader from "../../../../components/Loader";
import { RootState } from "../../../../redux";
import { Part, Store } from "../../../../redux/vehicles/actions";
import { updatePartsRequest } from "../../../../redux/parts/actions";
import { Field, renderField, getField } from "../../../../components/DetailsFormField";

const DETALIS_FIELDS: Field[] = [
    { n: "Name", f: "name", type: "text", editable: true },
    { n: "Quantity", f: "qty", type: "text", editable: true },
    { n: "Notes", f: "note", type: "textarea", editable: true },
    { n: "Price", f: "cost", type: "money", editable: true },
    { n: "Transport Company", f: "delivery_company", editable: true, type: "select", options: [
        { n: "Fedex", f: "delivery_company", v: "Fedex", type: "string" },
        { n: "UPS", f: "delivery_company", v: "UPS", type: "string" },
        { n: "USPS", f: "delivery_company", v: "USPS", type: "string" },
        { n: "DHL", f: "delivery_company", v: "DHL", type: "string" },
        { n: "Other", f: "delivery_company", v: "Other", type: "string" }
    ] },
    { n: "Tracking Number", f: "delivery_order", editable: true, type: "text" },
    { n: "Delivery Cost", f: "delivery_cost", editable: true, type: "money" },
    { n: "From Location", f: "delivery_from", editable: true, type: "text" },
    { n: "To Location", f: "store_id", editable: true, type: "select", options: [] },
    { n: "Est. Delivery Date", f: "delivery_est_date", editable: true, type: "date", hide: 'delivery_date' },
    { n: "Delivery Date", f: "delivery_date", editable: false, type: "text", show: "delivery_date" },
    { n: "Person Recieved", f: "signed_by.fullname", editable: false, type: "text" },
    { n: "Person Signature", f: "signature", editable: false, type: "image" },
];

const VEHICLE_FIELDS: Field[] = [
    { n: "Vin", f: "vehicle.vin", type: "text", editable: false },
    { n: "Make", f: "vehicle.make", type: "text", editable: false },
    { n: "Model", f: "vehicle.model", type: "text", editable: false },
    { n: "Year", f: "vehicle.year", type: "text", editable: false },
    { n: "Trim", f: "vehicle.trim", type: "text", editable: false },
    { n: "Body Style", f: "vehicle.body_style", type: "text", editable: false },
];

export function DashboardPartsInventoryTabDetails() {
    const dispatch = useDispatch();
    const { part } = useSelector((state: RootState) => state.parts);
    const [isEdit, setIsEdit] = useState(false);
    const [newPart, setNewPart] = useState<Part>({} as Part);
    const { stores } = useSelector((state: RootState) => state.vehicles);

    React.useEffect(() => {
        if (stores) {
            DETALIS_FIELDS.forEach((field) => {
                if (field.f === "store_id" && field.options?.length === 0) {
                    // @ts-ignore
                    field.options = stores.map((store: Store) => ({
                        n: `${store.title} ${store.location}`,
                        f: field.f,
                        v: store.id,
                        type: "string",
                    }));
                }
            });
        }
    }, [stores]);

    const saveEdit = () => {
        setIsEdit(false);

        const update_fields = Object.keys(newPart).filter(
            (key) => newPart[key as keyof Part] !== part?.[key as keyof Part]
        );

        if (update_fields.length === 0) return;

        dispatch(
            updatePartsRequest({
                parts: [newPart],
                update_fields: [
                    'id',
                    ...update_fields,
                ]
            })
        );
    };

    useEffect(() => {
        if (part) {
            setNewPart(part);
        }
    }, [part]);

    return !part || !stores ? (
        <Loader />
    ) : (
        <React.Fragment>
            <div key={newPart["id"]} className="flex flex-col gap-5">
                <Card className="p-2 md:p-5 gap-y-5 flex flex-col w-full overflow-visible">
                    <Card.Header>
                        <div className="flex justify-between">
                            <div className="">
                                <Typography className="block font-bold text-primary">
                                    Basic Information
                                </Typography>
                                <Typography type="small" className="block text-foreground">
                                    Update your profile information below.
                                </Typography>
                            </div>
                            {isEdit ? (
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        color="error"
                                        className="h-8 flex items-center gap-2"
                                        onClick={() => setIsEdit(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outline"
                                        color="success"
                                        className="h-8 flex items-center gap-2"
                                        onClick={saveEdit}
                                    >
                                        Save
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    color="secondary"
                                    className="h-8 flex items-center gap-2"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setNewPart(part);
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body className="!p-0 m-2">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 justify-stretch">
                            {DETALIS_FIELDS.filter((field) => {
                                return (
                                    (!field?.hide || !getField(field.hide, newPart)) &&
                                    (!field?.show || getField(field.show, newPart))
                                );
                            }).map((field, idx) => (
                                <div className="" key={idx}>
                                    {renderField(field, isEdit, newPart, setNewPart)}
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
                <Card className="p-2 md:p-5 gap-y-5 flex flex-col w-full">
                    <Card.Header>
                        <Typography className="block font-bold text-primary">
                            Vehicle Information
                        </Typography>
                    </Card.Header>
                    <Card.Body className="!p-0 m-2">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 justify-stretch">
                            {VEHICLE_FIELDS.map((field, idx) => (
                                <div className="" key={idx}>
                                    <Typography type="small" className="block text-foreground">
                                        {field.n}
                                    </Typography>
                                    <Typography className="block text-primary md:text-lg font-bold overflow-hidden text-ellipsis">
                                        {field.type === "text"
                                            ? getTextFormat(getField(field.f, newPart) as string)
                                            : field.type === "money"
                                            ? getMoneyFormat(getField(field.f, newPart) as string)
                                            : getField(field.f, newPart)}
                                    </Typography>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    );
}
